import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import api from "../lib/api";

const KIND_LABEL = {
  client_silent: "Went quiet",
  missing_care_plan: "Care plan",
  careplan_review_due: "Review due",
  first_visit_face_to_face: "Face-to-face",
  orientation_unsigned: "Orientation",
  intake_stalled: "Intake",
  careplan_signature: "Signature",
  agreement: "Agreement",
  questionnaire_gaps: "Questionnaire",
};

export default function ClientAttentionCard({ clientId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/attention", { params: { client_id: clientId } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [] }));
  }, [clientId]);

  if (!data) return null;
  const items = data.items || [];
  const intakeOpen = items.some((it) => it.kind === "questionnaire_gaps" || it.kind === "intake_stalled" || it.kind === "agreement");

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="client-attention-card">
      {intakeOpen && (
        <Link to={`/clients/intake/${clientId}`} data-testid="finish-intake-banner"
          className="flex items-center justify-between gap-3 border-b border-[#4A235A]/20 bg-[#4A235A] px-5 py-4 text-white transition-colors hover:bg-[#3a1c48]">
          <div>
            <p className="font-heading text-base font-semibold">Finish this client's intake</p>
            <p className="mt-0.5 text-xs text-white/75">Answer the remaining questions one at a time — everything prefills the paperwork automatically.</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#4A235A]">
            Continue intake <ArrowRight size={15} weight="bold" />
          </span>
        </Link>
      )}
      <div className="border-b border-stone-100 px-4 py-3">
        <p className="font-heading text-sm font-semibold text-stone-900">
          What this client needs from the office
          {items.length > 0 && <span className="ml-2 text-xs font-normal text-[#B7791F]">— {items.length} open</span>}
        </p>
      </div>
      {items.length === 0 ? (
        <p className="flex items-center gap-2 px-4 py-3 text-sm text-[#1e8449]" data-testid="client-attention-clear">
          <CheckCircle size={15} weight="fill" /> Nothing waiting — care plan, signatures, agreement and questionnaire are all in order.
        </p>
      ) : (
        <div className="divide-y divide-stone-50">
          {items.map((it, i) => (
            <Link key={`${it.kind}-${i}`} to={it.to || "#"}
              className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-primary/5"
              data-testid={`client-attention-${it.kind}`}>
              <span className="w-28 shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-center text-[10px] font-bold text-stone-500">
                {KIND_LABEL[it.kind] || it.kind}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-stone-700">
                <span className="font-medium text-stone-900">{it.who}</span> — {it.what}
              </span>
              {it.due && it.due !== "zzz" && <span className="shrink-0 text-xs font-semibold text-[#B7791F]">{it.due}</span>}
              <ArrowRight size={13} className="shrink-0 text-stone-300" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
